import { createFileRoute, Link, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import NotAvailable from '@/components/ui/NotAvailable'
import SectionCard from '@/components/ui/SectionCard'
import Skeleton from '@/components/ui/Skeleton'
import StepTimeline from '@/components/ui/StepTimeline'
import TitleRow from '@/components/ui/TitleRow'
import { DEMAND_AVAILABLE } from '@/features/ipo/api/ipoAvailability'
import { useIssuePeers, useSuspenseIssue } from '@/features/ipo/api/queries'
import IpoApplyRow from '@/features/ipo/components/IpoApplyRow'
import IpoBackButton from '@/features/ipo/components/IpoBackButton'
import IpoCompanyCard from '@/features/ipo/components/IpoCompanyCard'
import IpoDemandCard from '@/features/ipo/components/IpoDemandCard'
import IpoFactsGrid from '@/features/ipo/components/IpoFactsGrid'
import IpoInfoButton from '@/features/ipo/components/IpoInfoButton'
import IpoPeersCard from '@/features/ipo/components/IpoPeersCard'
import type { IssueStatus } from '@/schemas/ipo'
import { loadIpo } from '@/screens/ipo/IpoScreen.loader'

/**
 * The company's name is the one thing this header cannot know before the issue loads, and the
 * back button's target (the list of THIS issue's type) depends on it too, so the pending header
 * draws the boxed back's footprint and the title's. Same row as the loaded one (`items-center`,
 * a 36px title line) so the title lands where its placeholder was; the info circle is left out
 * (a route's `pendingComponent` is not code-split, see docs/route-loading.md, "Pitfalls").
 */
function IpoHeaderSkeleton() {
  return (
    <div className="flex items-center gap-4">
      <Skeleton className="size-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <Skeleton className="h-9 w-72 max-w-full rounded-sm" />
      </div>
    </div>
  )
}

/**
 * The loaded page's blocks, in order, 24 apart: the status line over the price line, the facts
 * panel (`IpoFactsGrid` — four cells), the calls to action, the dates card, and
 * the "company in numbers" card. `IpoPeersCard` is SECONDARY — started by the loader, never
 * awaited — and stays off this skeleton: it renders nothing at all when its own read comes back
 * with no peers, so a placeholder block here would grow the page by a section that may not
 * exist. It draws its own skeleton once mounted, same as every other secondary card.
 */
function IpoPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading the issue…" header={<IpoHeaderSkeleton />}>
      <div className="flex flex-col gap-3">
        <div className="flex h-4 items-center">
          <Skeleton className="h-3 w-16 rounded-xs" />
        </div>
        <div className="flex h-6 items-center">
          <Skeleton className="h-4 w-96 max-w-full rounded-xs" />
        </div>
      </div>
      <Skeleton className="h-20 w-full" />
      <div className="flex flex-wrap items-center gap-3">
        <Skeleton className="h-12 w-44" />
        <Skeleton className="h-12 w-44" />
      </div>
      <Skeleton className="h-34 w-full rounded-lg" />
      <Skeleton className="h-60 w-full rounded-lg" />
    </ScreenLayout>
  )
}

/** The header's own fallback, `data?.company ?? 'Public issue'` — known
 *  without data, so the error page (which never gets a company name) still
 *  carries the same back button and generic title the loaded page would
 *  show on its way in. */
const ERROR_HEADER = <TitleRow back="/market/issues" title="Public Issue" />

/**
 * A wrong or withdrawn key answers `ISSUE_NOT_FOUND` — mapped to `null` data
 * in `issueQueryOptions` rather than left to throw, so it never reaches this
 * component at all: `IpoScreen` keeps the not-found page as a `data === null`
 * branch, same as Stock's `company === null`. This is only ever a genuine
 * failure or an offline read, so it stays the light `RouteErrorPage` +
 * header the pending page already uses — an `errorComponent` is not
 * code-split, so it must not import anything heavier than that.
 */
function IpoError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={ERROR_HEADER} title="Couldn't load this issue" />
}

export const Route = createFileRoute('/_app/ipo/$key')({
  loader: loadIpo,
  pendingComponent: IpoPending,
  errorComponent: IpoError,
  component: IpoScreen,
})

/** The mock prints the status word alone, neutral: a window's state is not a price move. */
const STATUS_LABEL: Record<IssueStatus, string> = {
  open: 'Open',
  upcoming: 'Upcoming',
  closed: 'Closed',
}

function IpoScreen() {
  const { key } = Route.useParams()
  // Always settled: the loader awaited it, and a genuine first-load failure
  // is the route's `IpoError` (a suspense query only throws while it has no
  // data, so a failed background refetch keeps this page, same as before).
  // `null` is a real, non-error answer — a wrong or withdrawn key — mapped
  // in `issueQueryOptions` from the wire's `ISSUE_NOT_FOUND`; see Stock's
  // own `company === null` for the same shape.
  const { data } = useSuspenseIssue(key)
  const peers = useIssuePeers(key)

  if (data === null)
    return (
      <ScreenLayout header={ERROR_HEADER} pageTitle="Public Issue">
        <EmptyState
          kind="not-found"
          size="page"
          title="This issue doesn't exist"
          description="The link may be wrong, or the issue was withdrawn. Pick one from Public Issues."
          action={
            <Link to="/market/issues" className={stateActionClass('page')}>
              Browse Public Issues
            </Link>
          }
        />
      </ScreenLayout>
    )

  return (
    <ScreenLayout
      header={
        // The mock's header (`pi-dhead`): the boxed back button, the title, the info circle at the
        // far end. Back returns to the list of THIS issue's type.
        <div className="flex items-center gap-4">
          <IpoBackButton type={data.type} />
          <h1 className="min-w-0 flex-1 text-h-2xl text-foreground">{data.company}</h1>
          <IpoInfoButton type={data.type} />
        </div>
      }
      pageTitle={data.company}
    >
      {/* Status over the price line, 12 apart and 12 under the header (the mock's rhythm). */}
      <div className="-mt-3 flex flex-col gap-3">
        <p className="text-h-xs text-muted-foreground-subtle">{STATUS_LABEL[data.status]}</p>
        <p className="text-body-m text-muted-foreground">{data.priceLine}</p>
      </div>

      <IpoFactsGrid facts={data.facts} />

      <IpoApplyRow issue={data} />

      {data.timeline.length > 0 ? (
        <SectionCard eyebrow="The dates" className="[--section-card-pad-x:var(--space-4)]">
          <StepTimeline steps={data.timeline} />
        </SectionCard>
      ) : null}

      {/* Demand: mm-be serves no subscription figure yet (asks A5), so the card is built behind
          its switch and says so until it ships. */}
      {DEMAND_AVAILABLE && data.demand != null ? (
        <IpoDemandCard demand={data.demand} />
      ) : (
        <NotAvailable thing="Demand so far" />
      )}

      <IpoCompanyCard issue={data} />

      <IpoPeersCard
        peers={peers.data ?? []}
        sector={data.sector}
        state={peers.isError ? 'failed' : peers.data === undefined ? 'loading' : 'loaded'}
        onRetry={() => {
          void peers.refetch()
        }}
      />
    </ScreenLayout>
  )
}
